import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Card, Button } from '../components/common';
import { presetService } from '../services/presetService';
import { Preset } from '../types/preset';
import Wizard from '../components/preset/Wizard';

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
`;

const ErrorState = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
`;

const PresetEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchPreset(id);
    }
  }, [id]);

  const fetchPreset = async (presetId: string) => {
    try {
      setLoading(true);
      const fetchedPreset = await presetService.getPresetById(presetId);
      setPreset(fetchedPreset);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching preset:', err);
      
      // Handle 404 specifically - preset was likely deleted
      if (err.response?.status === 404) {
        setError('This preset has been deleted or no longer exists.');
        // Auto-redirect to presets list after a short delay
        setTimeout(() => {
          navigate('/admin/presets');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to load preset');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePresetUpdated = (updatedPreset: Preset | null) => {
    console.log('Preset updated/deleted:', updatedPreset);
    if (updatedPreset && updatedPreset.id) {
      // Navigate back to preset detail for updates
      navigate(`/admin/presets/${updatedPreset.id}`);
    } else {
      // Navigate back to preset list for deletions
      navigate('/admin/presets');
    }
  };

  const handleBack = () => {
    navigate('/admin/presets');
  };

  if (loading) {
    return (
      <EditContainer>
        <LoadingState>Loading preset...</LoadingState>
      </EditContainer>
    );
  }

  if (error) {
    return (
      <EditContainer>
        <ErrorState>
          <strong>Error:</strong> {error}
        </ErrorState>
        <Button onClick={handleBack} variant="secondary">
          Back to Presets
        </Button>
      </EditContainer>
    );
  }

  if (!preset) {
    return (
      <EditContainer>
        <ErrorState>
          <strong>Error:</strong> Preset not found
        </ErrorState>
        <Button onClick={handleBack} variant="secondary">
          Back to Presets
        </Button>
      </EditContainer>
    );
  }

  return (
    <EditContainer>
      <HeaderSection>
        <PageTitle>Edit Preset: {preset.name}</PageTitle>
        <BackButton onClick={handleBack} variant="secondary">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Presets
        </BackButton>
      </HeaderSection>

      <Card>
        <Wizard 
          onPresetCreated={handlePresetUpdated}
          selectedSymbol={preset.symbol}
          isInModal={false}
          editingPreset={preset}
        />
      </Card>
    </EditContainer>
  );
};

export default PresetEditPage; 